import { useBenefits } from '../BenefitsProvider'
import useTranslation from '../../shared/TranslationProvider'
import { useFormatTime } from '~/hooks/useFormatTime'

const useStepInfo = ({
  step,
  resumeReview,
  resumeRevisions,
  coach,
  meetingViaCalendly,
  t,
  serviceRequestList,
}) => {
  const { programLevel } = useBenefits()
  const { coachStarted } = resumeReview || {}
  const { locale } = useTranslation()
  const formatTime = useFormatTime(locale)
  const addBusinessDays = (date, days) => {
    let result = new Date(date)
    let addedDays = 0
    while (addedDays < days) {
      result.setDate(result.getDate() + 1)
      // Skip weekends (Saturday=6, Sunday=0)
      if (result.getDay() !== 0 && result.getDay() !== 6) {
        addedDays++
      }
    }
    return result
  }

  function revisionText() {
    if (resumeReview?.revision_declined) {
      return t(`${programLevel}.steps.askForRevisions.revision_declined`)
    } else if (
      resumeReview?.status == 'closed' &&
      resumeRevisions?.length <= 2
    ) {
      return t(`${programLevel}.steps.finalFeedback.title_closed`, {
        date: new Date(resumeReview?.completed_at).toLocaleDateString(),
      })
    } else if (
      resumeReview?.status === 'revision_returned' ||
      resumeReview?.status == 'closed'
    ) {
      return `Feedback Received ${
        resumeRevisions?.length > 0 &&
        formatTime(resumeRevisions[resumeRevisions?.length - 1]?.created_at)
      }`
    } else if (
      resumeRevisions[resumeRevisions?.length - 1]?.calendly_event
        ?.service_request_no_show_id
    ) {
      return t('no_show')
    } else if (
      (resumeReview?.status === 'revision_requested' ||
        resumeReview?.status == 'revision_meeting_requested') &&
      resumeRevisions[resumeRevisions?.length - 1]?.calendly_event != undefined
    ) {
      return t(`${programLevel}.steps.scheduleMeeting.meeting_scheduled`, {
        coach_name: coach.first_name,
        date: formatTime(
          resumeRevisions[resumeRevisions?.length - 1].calendly_event
            ?.start_time,
          'meeting_datetime',
        ),
      })
    } else if (
      (resumeReview?.status === 'revision_requested' &&
        !resumeRevisions[resumeRevisions?.length - 1]?.meetingViaCalendly) ||
      resumeReview?.status === 'closed'
    ) {
      return t(`${programLevel}.steps.askForRevisions.revision_submitted`, {
        coach_name: coach.first_name,
        date: formatTime(
          resumeRevisions[1]?.updated_at || resumeRevisions[1]?.created_at,
        ),
      })
    }
  }

  function meetingText() {
    const meetingIsCancelledByCoach =
      serviceRequestList['ServiceRequest::ResumeReview']?.status ===
        'cancelled' &&
      serviceRequestList['ServiceRequest::ResumeReview']?.cancelled_by_coach
    if (resumeRevisions?.length > 0) {
      const resumeReviewSR =
        serviceRequestList['ServiceRequest::ResumeReview'] || {}
      const meetingStartTime =
        resumeReviewSR.first_meeting_start_time ||
        resumeReviewSR.calendly_event?.start_time
      if (meetingStartTime === undefined) {
        return null
      }
      return t(`${programLevel}.steps.scheduleMeeting.meeting_complete`, {
        coach_name: coach.first_name,
        date: formatTime(meetingStartTime, 'short'),
      })
    }
    if (meetingIsCancelledByCoach) {
      return t('coach_cancelled', {
        coachName: coach.first_name,
      })
    }
    if (
      serviceRequestList['ServiceRequest::ResumeReview']?.calendly_event
        ?.service_request_no_show_id
    ) {
      return t('no_show')
    } else if (
      serviceRequestList['ServiceRequest::ResumeReview']?.calendly_event
    ) {
      return t(`${programLevel}.steps.scheduleMeeting.meeting_scheduled`, {
        coach_name: coach.first_name,
        date: formatTime(
          serviceRequestList['ServiceRequest::ResumeReview']?.calendly_event
            ?.start_time,
          'meeting_datetime',
        ),
      })
    } else if (
      resumeReview?.status === 'assigned' ||
      resumeReview?.status === 'cancelled'
    ) {
      return !meetingViaCalendly
        ? t(`${programLevel}.steps.scheduleMeeting.coach_assigned_no_meeting`, {
            coach_name: coach.first_name,
            date: formatTime(
              serviceRequestList['ServiceRequest::ResumeReview']?.calendly_event
                ?.start_time,
              'short',
            ),
          })
        : t(`${programLevel}.steps.scheduleMeeting.coach_assigned`, {
            coach_name: coach.first_name,
          })
    } else {
      return t(`${programLevel}.steps.scheduleMeeting.coach_pending`, {
        date: formatTime(
          addBusinessDays(
            resumeReview?.updated_at || resumeReview?.created_at,
            meetingViaCalendly ? 2 : 3,
          ),
          'short',
        ),
      })
    }
  }

  const receiveFeedbackText = () => {
    if (resumeReview?.status === 'closed' && resumeRevisions?.length <= 1) {
      return t(`${programLevel}.steps.finalFeedback.title_closed`, {
        date: new Date(resumeReview?.completed_at).toLocaleDateString(),
      })
    }
    if (!meetingViaCalendly && !resumeRevisions?.length) {
      return meetingText()
    }
    return t(`${programLevel}.steps.finalFeedback.feedback_received`, {
      date:
        resumeRevisions?.length > 0 &&
        formatTime(resumeRevisions[0]?.created_at),
    })
  }

  const selectionText = coachStarted
    ? t(`${programLevel}.steps.finalFeedback.user_selection`)
    : ''

  const chosenReview = resumeReview?.meeting_via_calendly
    ? t(`${programLevel}.steps.finalFeedback.meeting_with_coach`)
    : t(`${programLevel}.steps.finalFeedback.platform_review`)

  const stepInfo = {
    chooseReviewType: `${selectionText}${chosenReview}`,
    submitResume: t(`${programLevel}.steps.submitResume.submitted_at`, {
      date: formatTime(resumeReview?.created_at, 'short'),
    }),
    scheduleMeeting: meetingText(),
    receiveFeedback: receiveFeedbackText(),
    askForRevisions: revisionText(),
  }
  return stepInfo[step]
}

export default useStepInfo
